import { createFileRoute, isNotFound } from '@tanstack/react-router';
import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { ensureSeoData } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { Suspense } from 'react';
import { resolveSlugSeo, Seo } from '../-components/seo';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { preloadImageOnClient, heroImageSize } from '@worldcoo/ui/lib/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { slugs as slugsByLocale } from './privacy-policy.meta';

export const Route = createFileRoute('/$portalId/privacy-policy')({
	loader: async ({ context }) => {
		context.queryClient
			.ensureQueryData(trpc.portal.getAuthor.queryOptions({ locale: context.locale, portalId: context.portalId }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				const logoHeight = getLogoImageSize(context.portalId).height;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: logoHeight,
						width: Math.round(logoHeight * (data.logo.width / data.logo.height)),
						format: 'avif',
						quality: 60,
					}),
				);
			});

		context.queryClient.ensureQueryData(
			trpc.core.getLegalContent.queryOptions({ type: 'privacy-policy', locale: context.locale }),
		);

		await ensureSeoData(
			context,
			context.queryClient
				.ensureQueryData(trpc.portal.getMetadata.queryOptions({ locale: context.locale, portalId: context.portalId }))
				.then((data) => {
					if ('notFound' in data) return;
					if (!data.media?.location) return;
					preloadImageOnClient(
						generateImageSrc(data.media.location, { ...heroImageSize, format: 'avif', quality: 60 }),
					);
				}),
		);
	},
	component: Component,
});

function Component() {
	const context = Route.useRouteContext();
	const { data: html } = useSuspenseQuery(
		trpc.core.getLegalContent.queryOptions({ type: 'privacy-policy', locale: context.locale }),
	);

	return (
		<>
			<Suspense>
				<SuspensedHead />
			</Suspense>
			<main className="w-full">
				<Header />
				<Suspense>
					<PrivacyContent html={html} />
				</Suspense>
				<Footer />
			</main>
		</>
	);
}

function PrivacyContent({ html }: { html: string }) {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	return <article className="unreset-tailwind mx-auto max-w-4xl p-8" dangerouslySetInnerHTML={{ __html: html }} />;
}

function SuspensedHead() {
	const { baseUrl, locale } = Route.useRouteContext();
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	const { canonicalUrl, alternates } = resolveSlugSeo(baseUrl, locale, metadata.locales, slugsByLocale);

	return (
		<Seo
			title={metadata.texts.title}
			description={metadata.texts.presentation}
			image={metadata.media?.location ? generateImageSrc(metadata.media.location) : undefined}
			canonicalUrl={canonicalUrl}
			alternates={alternates}
			locale={locale}
			alternateLocales={metadata.locales}
		/>
	);
}
